import { useState } from 'react';
import { Badge, Card, CardContent, Spinner } from '@/components/ui';
import { useFamilyMaturity } from '@/hooks/use-family-maturity';
import type { IFamilyMaturity, MaturityStatus } from '@skystone/shared';

/**
 * Family-maturity admin editor (PRODOPSAI-391 / US5).
 *
 * Appended to /admin/metric-definitions below the existing metric-group
 * sections. Each row shows a family label, a status badge in the colour
 * matching the source mockup (`Implemented` navy / `InProgress` amber /
 * `NotStarted` red / `Prioritized` grey) and a `<select>` with the four
 * options. Status changes auto-save on `change`; success or failure is
 * communicated inline next to the row so the section feels native to the
 * existing per-metric editor UX.
 */

const STATUS_OPTIONS: readonly MaturityStatus[] = [
  'Implemented',
  'InProgress',
  'NotStarted',
  'Prioritized',
];

const STATUS_LABEL: Record<MaturityStatus, string> = {
  Implemented: 'Implemented',
  InProgress: 'In Progress',
  NotStarted: 'Not Started',
  Prioritized: 'Prioritized',
};

// Map maturity statuses to existing Badge variants. Note: the Badge component
// has no dedicated `muted` or `primary` variant — `default` already renders
// the primary navy palette and `secondary` provides the muted grey tone, so
// these are the closest matches to the source mockup's status pills.
const STATUS_VARIANT: Record<MaturityStatus, 'default' | 'warning' | 'error' | 'secondary'> = {
  Implemented: 'default',
  InProgress: 'warning',
  NotStarted: 'error',
  Prioritized: 'secondary',
};

interface RowState {
  saving: boolean;
  saveSuccess: boolean;
  error: string | null;
}

const INITIAL_ROW_STATE: RowState = {
  saving: false,
  saveSuccess: false,
  error: null,
};

interface FamilyMaturityRowProps {
  row: IFamilyMaturity;
  state: RowState;
  onChange: (familyCode: string, status: MaturityStatus) => void;
}

function FamilyMaturityRow({ row, state, onChange }: Readonly<FamilyMaturityRowProps>) {
  const selectId = `family-maturity-status-${row.familyCode}`;
  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-background p-3">
      <p className="flex-1 text-sm font-medium text-foreground">{row.label}</p>
      <Badge variant={STATUS_VARIANT[row.status]}>{STATUS_LABEL[row.status]}</Badge>
      <label className="sr-only" htmlFor={selectId}>
        Status for {row.label}
      </label>
      <select
        id={selectId}
        className="rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
        value={row.status}
        disabled={state.saving}
        onChange={(e) => onChange(row.familyCode, e.target.value as MaturityStatus)}
      >
        {STATUS_OPTIONS.map((status) => (
          <option key={status} value={status}>
            {STATUS_LABEL[status]}
          </option>
        ))}
      </select>
      <span className="w-20 text-right text-xs">
        {state.saving && <span className="text-muted-foreground">Saving…</span>}
        {state.saveSuccess && (
          <span className="text-success-700" aria-live="polite">Saved</span>
        )}
        {state.error && (
          <span className="text-error" role="alert">{state.error}</span>
        )}
      </span>
    </div>
  );
}

export function FamilyMaturitySection() {
  const { rows, isLoading, error, updateStatus } = useFamilyMaturity();
  const [rowState, setRowState] = useState<Record<string, RowState>>({});

  function patchRowState(familyCode: string, patch: Partial<RowState>) {
    setRowState((prev) => ({
      ...prev,
      [familyCode]: { ...INITIAL_ROW_STATE, ...prev[familyCode], ...patch },
    }));
  }

  async function handleChange(familyCode: string, status: MaturityStatus) {
    patchRowState(familyCode, { saving: true, saveSuccess: false, error: null });
    try {
      await updateStatus(familyCode, status);
      patchRowState(familyCode, { saving: false, saveSuccess: true, error: null });
      // Auto-clear the saved indicator after a couple of seconds — matches
      // the pattern in MetricDefinitionEditor.
      setTimeout(() => {
        patchRowState(familyCode, { saveSuccess: false });
      }, 2000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Save failed';
      patchRowState(familyCode, { saving: false, saveSuccess: false, error: message });
    }
  }

  return (
    <section className="space-y-3" aria-labelledby="family-maturity-heading">
      <div>
        <h2
          id="family-maturity-heading"
          className="text-sm font-bold text-foreground"
        >
          🏗️ Family Maturity
        </h2>
        <p className="text-xs text-muted-foreground">
          Status shown to leadership on Organisational View → Overview
        </p>
      </div>
      {error && (
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-error" role="alert">{error}</p>
          </CardContent>
        </Card>
      )}
      {isLoading ? (
        <div className="flex items-center justify-center py-6"><Spinner /></div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <FamilyMaturityRow
              key={row.familyCode}
              row={row}
              state={rowState[row.familyCode] ?? INITIAL_ROW_STATE}
              onChange={handleChange}
            />
          ))}
        </div>
      )}
    </section>
  );
}
